import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, CheckCircle2, Circle, Star } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { AdminPage, SaveBar, SectionCard } from '../../../components/design-system/admin';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { PlanFeatures } from '../../../modules/superadmin/our-vendors/models/vendor.model';
import {
  CATALOG_MODULE,
  DEPTH_MODULE,
  PLAN_ADDONS,
  PLAN_MODULES,
  QUALITIES,
  addonEnabled,
} from '../../../modules/superadmin/plans/plan-catalog';
import type { PlanAddon } from '../../../modules/superadmin/plans/plan-catalog';
import { usePlanLabels } from '../../../modules/superadmin/plans/use-plan-labels';
import { PlanIcon } from '../../../modules/superadmin/plans/PlanIcon';
import { PlanLimitField } from '../../../modules/superadmin/plans/PlanLimitField';
import { PlanModuleBlock } from '../../../modules/superadmin/plans/PlanModuleBlock';

type Quality = 'low' | 'medium' | 'high';
type FeatureKey = keyof Omit<PlanFeatures, 'visualizationQuality'>;
type PricingType = 'free' | 'trial' | 'paid' | 'custom';

const DEFAULT_FORM = {
  name: '',
  slug: '',
  description: '',
  isDefault: false,
  sortOrder: 0,
  pricingType: 'paid' as PricingType,
  price: 0,
  annualPrice: 0,
  currency: 'usd',
  // Pricing v2 (docs/pricing-v2.md): offered on the upgrade page, the line
  // under its name, and a trial's length.
  isPublic: true,
  tagline: '',
  trialDays: 14,
  integrationFee: 0,
  stripePriceId: '',
  stripeProductId: '',
  limits: {
    maxProducts: 50,
    maxSpaces: 5,
    maxEmailsPerMonth: 100,
    maxSyncFlowsPerDay: 5,
    maxAutomationRules: 2,
    maxLeads: 50,
    maxImportProducts: 0,
    monthlyCredits: 0,
    maxSocialAccounts: 0,
    maxScheduledPostsPerMonth: 0,
    maxVisualizationsPerMonth: -1,
    maxSeats: -1,
    // The Free plan's depth limits: unlimited unless a plan sets them.
    maxRoomAnalysesPerMonth: -1,
    maxShopperChatsPerMonth: -1,
    maxSeamlessPerMonth: -1,
    max3dModelsPerMonth: -1,
    maxBrandReadsPerMonth: -1,
    maxMarketRefreshesPerMonth: -1,
    maxKeywordRows: -1,
    maxCompetitorRows: -1,
    maxGapRows: -1,
    maxConversationsVisible: -1,
    analyticsHistoryDays: -1,
  },
  features: {
    apiIntegration: false,
    emailAutomation: false,
    aiSpaceAnalysis: true,
    seamlessGeneration: true,
    model3dGeneration: false,
    advancedAnalytics: false,
    customFields: false,
    categoryManagement: false,
    productCardCustomization: false,
    contentStudio: false,
    socialScheduler: false,
    shopperChat: false,
    adsManager: false,
    aiProductEnrichment: false,
    visualizationQuality: 'low' as Quality,
  },
};

type FormShape = typeof DEFAULT_FORM;
type LimitKey = keyof FormShape['limits'];

const ALL_LIMIT_KEYS = Object.keys(DEFAULT_FORM.limits) as LimitKey[];
const ALL_FEATURE_KEYS = (Object.keys(DEFAULT_FORM.features) as (keyof FormShape['features'])[])
  .filter((k) => k !== 'visualizationQuality') as FeatureKey[];

// Derive the billing model for a plan saved before `pricingType` existed.
const derivePricingType = (data: any): PricingType => {
  if (data?.pricingType) return data.pricingType;
  // By slug, not by isDefault: the default plan is unlimited Enterprise, not a free one.
  if (data?.slug === 'free') return 'free';
  if (data?.stripePriceId || Number(data?.price) > 0) return 'paid';
  return 'custom';
};

const CATALOG_EXPAND_KEY = '__catalog';
const DEPTH_EXPAND_KEY = '__depth';

/** Create or edit a plan: what it is called, what it costs, what it includes and how much of it. */
export const EditPlanPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { planId } = useParams<{ planId: string }>();
  const plansApi = usePlansApi();
  const labels = usePlanLabels();

  const isNew = !planId || planId === 'new';
  const [form, setForm] = useState<FormShape>(DEFAULT_FORM);
  // The form as loaded, so the save bar can say whether anything changed.
  const [baseline, setBaseline] = useState<FormShape>(DEFAULT_FORM);
  // Snapshot of the loaded plan's price/currency, to detect changes on save.
  const loadedRef = useRef<{ price?: number; currency?: string } | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  // Feature-module accordions — expanded by default so the admin can edit limits.
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const seed: Record<string, boolean> = { [CATALOG_EXPAND_KEY]: true };
    PLAN_MODULES.forEach((m) => {
      seed[m.feature!] = true;
    });
    return seed;
  });

  useEffect(() => {
    if (!isNew && planId) {
      plansApi
        .getPlan(planId)
        .then((data: any) => {
          if (data) {
            const loaded: FormShape = {
              ...DEFAULT_FORM,
              ...data,
              pricingType: derivePricingType(data),
              limits: { ...DEFAULT_FORM.limits, ...data.limits },
              features: { ...DEFAULT_FORM.features, ...data.features },
            };
            setForm(loaded);
            setBaseline(loaded);
            loadedRef.current = { price: data.price, currency: data.currency };
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Normalise billing fields to the chosen model before persisting.
      const payload: FormShape = {
        ...form,
        // Paid: the monthly price. Custom: the "from" floor the upgrade page
        // shows (Enterprise from $2,000). Free and trial cost nothing.
        price: form.pricingType === 'paid' || form.pricingType === 'custom' ? Number(form.price) || 0 : 0,
        annualPrice: form.pricingType === 'paid' || form.pricingType === 'custom' ? Number(form.annualPrice) || 0 : 0,
        // A free plan or a trial can never carry a setup fee; paid and custom plans can.
        integrationFee: form.pricingType === 'free' || form.pricingType === 'trial' ? 0 : Number(form.integrationFee) || 0,
        trialDays: Math.max(1, Math.round(Number(form.trialDays) || 14)),
      };
      // A paid plan with a positive price is a self-serve Stripe plan; when its
      // price/currency changes (or it has no Stripe price yet) create a fresh
      // Stripe price on the plan's product and repoint the plan.
      const isPaid = payload.pricingType === 'paid' && Number(payload.price) > 0;
      if (isNew) {
        const created: any = await plansApi.createPlan(payload);
        const newId = created?._id;
        if (newId && isPaid) await plansApi.setPlanStripePrice(newId, Number(payload.price), payload.currency);
      } else {
        await plansApi.updatePlan(planId!, payload);
        const prev = loadedRef.current;
        const priceChanged = Number(payload.price) !== Number(prev?.price) || (payload.currency || '') !== (prev?.currency || '');
        if (isPaid && (priceChanged || !payload.stripePriceId)) {
          await plansApi.setPlanStripePrice(planId!, Number(payload.price), payload.currency);
        }
      }
      toast.success(isNew ? t('ADMIN_PLANS.EDIT.CREATED') : t('ADMIN_PLANS.EDIT.UPDATED'));
      setTimeout(() => navigate('/plans'), 600);
    } catch (err: any) {
      const fallback = t('ADMIN_PLANS.EDIT.SAVE_ERROR');
      let message: string = fallback;
      try {
        if (err && typeof err.json === 'function') {
          const body = await err.json();
          const raw = body?.message ?? body?.error ?? fallback;
          message = Array.isArray(raw) ? raw.join(' • ') : String(raw);
        } else if (err?.response?.data?.message) {
          const raw = err.response.data.message;
          message = Array.isArray(raw) ? raw.join(' • ') : String(raw);
        } else if (err?.message) {
          message = String(err.message);
        }
      } catch {
        // body wasn't JSON — keep fallback
      }
      toast.error(message);
      // eslint-disable-next-line no-console
      console.error('[edit-plan] save failed', err);
      setSaving(false);
    }
  };

  const updateField = (field: string, value: any) => setForm((prev) => ({ ...prev, [field]: value }));

  // Switching to Custom (Enterprise / price-not-fixed) seeds an all-inclusive
  // plan by default: every limit unlimited (-1), every feature enabled, top
  // render quality. The admin can still tune afterwards.
  const changePricingType = (v: PricingType) =>
    setForm((prev) => {
      if (v !== 'custom') return { ...prev, pricingType: v };
      const limits = { ...prev.limits };
      ALL_LIMIT_KEYS.forEach((k) => {
        limits[k] = -1;
      });
      const features = { ...prev.features };
      ALL_FEATURE_KEYS.forEach((k) => {
        (features as any)[k] = true;
      });
      features.visualizationQuality = 'high';
      return { ...prev, pricingType: v, limits, features };
    });
  const updateLimit = (key: LimitKey, value: number) =>
    setForm((prev) => ({ ...prev, limits: { ...prev.limits, [key]: value } }));
  const updateFeature = (key: string, value: any) =>
    setForm((prev) => ({ ...prev, features: { ...prev.features, [key]: value } }));
  const toggleExpand = (key: string) => setExpanded((p) => ({ ...p, [key]: !p[key] }));
  // An add-on bundle switches all its features together.
  const toggleAddon = (addon: PlanAddon) =>
    setForm((p) => {
      const on = addonEnabled(p.features, addon);
      const next = { ...p, features: { ...p.features } };
      addon.keys.forEach((k) => {
        (next.features as any)[k] = !on;
      });
      return next;
    });

  const title = isNew ? t('PLANS.ADMIN.CREATE') : t('PLANS.ADMIN.EDIT');
  const parents = [{ label: t('ADMIN_SHELL.NAV.PLANS'), href: '/plans' }];
  const backButton = (
    <Button variant="ghost" className="text-muted-foreground" onClick={() => navigate('/plans')} data-testid="back-button">
      <ArrowLeft className="rtl:rotate-180" />
      {t('COMMON.BACK')}
    </Button>
  );

  if (loading) {
    return (
      <AdminPage title={title} parents={parents} actions={backButton}>
        <div className="flex flex-col gap-4" data-testid="edit-plan-loading" aria-busy="true">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Skeleton className="h-64 rounded-xl" />
            <Skeleton className="h-64 rounded-xl" />
          </div>
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </AdminPage>
    );
  }

  const pricingOptions: { value: PricingType; label: string; help: string }[] = [
    { value: 'free', label: t('PLANS.ADMIN.FORM.BILLING_FREE'), help: t('PLANS.ADMIN.FORM.BILLING_FREE_HELP') },
    { value: 'trial', label: t('PRICING.ADMIN_PLAN.TRIAL'), help: t('PRICING.ADMIN_PLAN.TRIAL_HELP') },
    { value: 'paid', label: t('PLANS.ADMIN.FORM.BILLING_PAID'), help: t('PLANS.ADMIN.FORM.BILLING_PAID_HELP') },
    { value: 'custom', label: t('PLANS.ADMIN.FORM.BILLING_CUSTOM'), help: t('PLANS.ADMIN.FORM.BILLING_CUSTOM_HELP') },
  ];
  // Free-forever is retired (docs/pricing-v2.md): only a plan still saved as Free shows the option.
  const shownPricingOptions = pricingOptions.filter((o) => o.value !== 'free' || form.pricingType === 'free');
  const currentPricingHelp = pricingOptions.find((o) => o.value === form.pricingType)?.help || '';
  const missingRequired = !form.name || !form.slug;
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const limitFields = (keys: LimitKey[]) => (
    <div className="grid grid-cols-1 gap-x-8 gap-y-5 lg:grid-cols-2">
      {keys.map((k) => (
        <PlanLimitField key={k} limitKey={k} value={form.limits[k]} onChange={(v) => updateLimit(k, v)} />
      ))}
    </div>
  );

  return (
    <AdminPage
      title={title}
      description={isNew ? t('ADMIN_PLANS.EDIT.DESCRIPTION_NEW') : t('ADMIN_PLANS.EDIT.DESCRIPTION')}
      parents={parents}
      actions={
        <>
          {backButton}
          {/* Default plan for new vendors (and vendors without a plan). */}
          <Button
            variant="outline"
            onClick={() => updateField('isDefault', !form.isDefault)}
            data-testid="default-button"
            aria-pressed={form.isDefault}
            className={cn(form.isDefault && 'border-foreground')}
          >
            <Star className={cn(form.isDefault && 'fill-current')} />
            {form.isDefault ? t('PLANS.ADMIN.FORM.IS_DEFAULT') : t('PLANS.ADMIN.FORM.SET_DEFAULT')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* General */}
        <SectionCard title={t('PLANS.ADMIN.FORM.GENERAL')} description={t('ADMIN_PLANS.EDIT.GENERAL_DESC')}>
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="plan-name" className="text-xs text-muted-foreground">
                  {t('PLANS.ADMIN.FORM.NAME')}
                </Label>
                <Input id="plan-name" value={form.name} onChange={(e) => updateField('name', e.target.value)} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="plan-slug" className="text-xs text-muted-foreground">
                  {t('PLANS.ADMIN.FORM.SLUG')}
                </Label>
                <Input
                  id="plan-slug"
                  className="font-mono"
                  value={form.slug}
                  onChange={(e) => updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  aria-describedby="plan-slug-help"
                />
                <p id="plan-slug-help" className="text-xs text-muted-foreground">
                  {t('PLANS.ADMIN.FORM.SLUG_HELP')}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="plan-description" className="text-xs text-muted-foreground">
                {t('PLANS.ADMIN.FORM.DESCRIPTION')}
              </Label>
              <Textarea
                id="plan-description"
                rows={3}
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="plan-tagline" className="text-xs text-muted-foreground">
                {t('PRICING.ADMIN_PLAN.TAGLINE')}
              </Label>
              <Input
                id="plan-tagline"
                value={form.tagline || ''}
                placeholder={t('PRICING.ADMIN_PLAN.TAGLINE_PLACEHOLDER')}
                onChange={(e) => updateField('tagline', e.target.value)}
                data-testid="tagline-input"
              />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{t('PRICING.ADMIN_PLAN.PUBLIC')}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{t('PRICING.ADMIN_PLAN.PUBLIC_HELP')}</p>
              </div>
              <Switch
                checked={form.isPublic !== false}
                onCheckedChange={(v) => updateField('isPublic', v)}
                aria-label={t('PRICING.ADMIN_PLAN.PUBLIC')}
                data-testid="public-switch"
              />
            </div>
          </div>
        </SectionCard>

        {/* Billing */}
        <SectionCard title={t('PLANS.ADMIN.FORM.BILLING')} description={t('ADMIN_PLANS.EDIT.BILLING_DESC')}>
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <ToggleGroup
                type="single"
                variant="outline"
                value={form.pricingType}
                onValueChange={(v) => v && changePricingType(v as PricingType)}
                data-testid="pricing-type-group"
                aria-label={t('PLANS.ADMIN.FORM.BILLING')}
              >
                {shownPricingOptions.map((o) => (
                  <ToggleGroupItem key={o.value} value={o.value} data-testid={`pricing-type-${o.value}`} className="px-5">
                    {o.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <p className="text-xs text-muted-foreground">{currentPricingHelp}</p>
            </div>

            {/* Paid → price + currency; the Stripe price is created/repointed
                automatically on save, so the IDs are shown read-only for reference. */}
            {form.pricingType === 'trial' && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="plan-trial-days" className="text-xs text-muted-foreground">
                  {t('PRICING.ADMIN_PLAN.TRIAL_DAYS')}
                </Label>
                <Input
                  id="plan-trial-days"
                  type="number"
                  min={1}
                  max={365}
                  className="w-40 tabular-nums"
                  value={form.trialDays}
                  onChange={(e) => updateField('trialDays', parseInt(e.target.value, 10) || 14)}
                  data-testid="trial-days-input"
                />
                <p className="text-xs text-muted-foreground">{t('PRICING.ADMIN_PLAN.TRIAL_DAYS_HELP')}</p>
              </div>
            )}

            {(form.pricingType === 'paid' || form.pricingType === 'custom') && (
              <>
                <div className="flex flex-wrap items-start gap-4">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="plan-price" className="text-xs text-muted-foreground">
                      {form.pricingType === 'custom' ? t('PRICING.ADMIN_PLAN.FLOOR_PRICE') : t('PLANS.ADMIN.FORM.PRICE')}
                    </Label>
                    <Input
                      id="plan-price"
                      type="number"
                      min={0}
                      className="w-40 tabular-nums"
                      value={form.price}
                      onChange={(e) => updateField('price', parseFloat(e.target.value) || 0)}
                      data-testid="price-input"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="plan-currency" className="text-xs text-muted-foreground">
                      {t('PLANS.ADMIN.FORM.CURRENCY')}
                    </Label>
                    <Input
                      id="plan-currency"
                      className="w-28 uppercase"
                      value={form.currency}
                      onChange={(e) => updateField('currency', e.target.value.toLowerCase())}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="plan-annual-price" className="text-xs text-muted-foreground">
                      {t('PRICING.ADMIN_PLAN.ANNUAL_PRICE')}
                    </Label>
                    <Input
                      id="plan-annual-price"
                      type="number"
                      min={0}
                      className="w-40 tabular-nums"
                      value={form.annualPrice || 0}
                      onChange={(e) => updateField('annualPrice', parseFloat(e.target.value) || 0)}
                      data-testid="annual-price-input"
                    />
                  </div>
                </div>
                <p className="-mt-2 text-xs text-muted-foreground">
                  {form.pricingType === 'custom' ? t('PRICING.ADMIN_PLAN.FLOOR_HELP') : t('PRICING.ADMIN_PLAN.ANNUAL_HELP')}
                </p>
                {(form.stripePriceId || form.stripeProductId) && (
                  <div className="rounded-lg bg-muted/60 p-3 text-xs">
                    <p className="mb-1.5 text-muted-foreground">{t('PLANS.ADMIN.FORM.STRIPE_LINKED')}</p>
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                      {form.stripePriceId && (
                        <>
                          <dt className="text-muted-foreground">{t('ADMIN_PLANS.EDIT.STRIPE_PRICE')}</dt>
                          <dd className="truncate font-mono">{form.stripePriceId}</dd>
                        </>
                      )}
                      {form.stripeProductId && (
                        <>
                          <dt className="text-muted-foreground">{t('ADMIN_PLANS.EDIT.STRIPE_PRODUCT')}</dt>
                          <dd className="truncate font-mono">{form.stripeProductId}</dd>
                        </>
                      )}
                    </dl>
                  </div>
                )}
              </>
            )}

            {/* One-time integration fee — applies to paid and custom (sales-led)
                plans alike; a quote can override or waive it per deal. */}
            {form.pricingType !== 'free' && form.pricingType !== 'trial' && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="plan-integration-fee" className="text-xs text-muted-foreground">
                  {t('ADMIN_PLANS.EDIT.INTEGRATION_FEE')}
                </Label>
                <Input
                  id="plan-integration-fee"
                  type="number"
                  min={0}
                  className="w-40 tabular-nums"
                  value={form.integrationFee}
                  onChange={(e) => updateField('integrationFee', parseFloat(e.target.value) || 0)}
                  data-testid="integration-fee-input"
                  aria-describedby="plan-integration-fee-help"
                />
                <p id="plan-integration-fee-help" className="text-xs text-muted-foreground">
                  {t('ADMIN_PLANS.EDIT.INTEGRATION_FEE_HELP')}
                </p>
              </div>
            )}
          </div>
        </SectionCard>
      </div>

      {/* Features & limits — grouped like the vendor quote configurator */}
      <SectionCard title={t('PLANS.ADMIN.FORM.FEATURES_LIMITS')} description={t('ADMIN_PLANS.EDIT.FEATURES_DESC')}>
        <div className="divide-y">
          {PLAN_MODULES.map((m) => {
            const feature = m.feature!;
            const moduleTitle = labels.moduleTitle(m);
            const on = Boolean(form.features[feature]);
            return (
              <PlanModuleBlock
                key={m.id}
                Icon={m.Icon}
                title={moduleTitle}
                description={labels.moduleDesc(m)}
                control={
                  <Switch
                    checked={on}
                    onCheckedChange={(v) => updateFeature(feature, v)}
                    aria-label={moduleTitle}
                    data-testid={`feature-switch-${feature}`}
                  />
                }
                expanded={!!expanded[feature]}
                onToggleExpand={() => toggleExpand(feature)}
                configureLabel={t('ADMIN_PLANS.EDIT.CONFIGURE', { name: moduleTitle })}
                dimmed={!on}
              >
                {limitFields(m.limits as LimitKey[])}
                {(m.quality || m.extras.length > 0) && (
                  <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                    {m.quality && (
                      <div className="flex items-center gap-2.5">
                        <span className="text-sm text-muted-foreground">{t('PLANS.ADMIN.FORM.VIZ_QUALITY')}</span>
                        <ToggleGroup
                          type="single"
                          variant="outline"
                          size="sm"
                          value={form.features.visualizationQuality}
                          onValueChange={(v) => v && updateFeature('visualizationQuality', v)}
                          aria-label={t('PLANS.ADMIN.FORM.VIZ_QUALITY')}
                        >
                          {QUALITIES.map((q) => (
                            <ToggleGroupItem key={q} value={q} aria-label={labels.qualityFull(q)}>
                              {labels.quality(q)}
                            </ToggleGroupItem>
                          ))}
                        </ToggleGroup>
                      </div>
                    )}
                    {m.extras.map((extra) => (
                      <div key={extra} className="flex items-center gap-2">
                        <Switch
                          id={`plan-feature-${extra}`}
                          checked={Boolean(form.features[extra])}
                          onCheckedChange={(v) => updateFeature(extra, v)}
                          aria-label={labels.featureLabel(extra)}
                          data-testid={`feature-switch-${extra}`}
                        />
                        <Label htmlFor={`plan-feature-${extra}`} className="text-sm font-normal text-muted-foreground">
                          {labels.featureLabel(extra)}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </PlanModuleBlock>
            );
          })}

          {/* Catalog & team — always included */}
          <PlanModuleBlock
            Icon={CATALOG_MODULE.Icon}
            title={labels.moduleTitle(CATALOG_MODULE)}
            description={labels.moduleDesc(CATALOG_MODULE)}
            control={<Badge variant="secondary">{t('ADMIN_PLANS.INCLUDED')}</Badge>}
            expanded={!!expanded[CATALOG_EXPAND_KEY]}
            onToggleExpand={() => toggleExpand(CATALOG_EXPAND_KEY)}
            configureLabel={t('ADMIN_PLANS.EDIT.CONFIGURE', { name: labels.moduleTitle(CATALOG_MODULE) })}
          >
            {limitFields(CATALOG_MODULE.limits as LimitKey[])}
          </PlanModuleBlock>

          {/* Free-plan depth — meters on paid paths and rows shown; collapsed, as only Free sets them */}
          <PlanModuleBlock
            Icon={DEPTH_MODULE.Icon}
            title={labels.moduleTitle(DEPTH_MODULE)}
            description={labels.moduleDesc(DEPTH_MODULE)}
            control={<Badge variant="secondary">{t('ADMIN_PLANS.INCLUDED')}</Badge>}
            expanded={!!expanded[DEPTH_EXPAND_KEY]}
            onToggleExpand={() => toggleExpand(DEPTH_EXPAND_KEY)}
            configureLabel={t('ADMIN_PLANS.EDIT.CONFIGURE', { name: labels.moduleTitle(DEPTH_MODULE) })}
          >
            {limitFields(DEPTH_MODULE.limits as LimitKey[])}
          </PlanModuleBlock>
        </div>
      </SectionCard>

      {/* Add-ons */}
      <SectionCard title={t('ADMIN_PLANS.ADDONS')} description={t('ADMIN_PLANS.EDIT.ADDONS_DESC')}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {PLAN_ADDONS.map((a) => {
            const on = addonEnabled(form.features, a);
            const addonTitle = labels.addonTitle(a);
            return (
              <button
                key={a.id}
                type="button"
                aria-label={addonTitle}
                aria-pressed={on}
                data-testid={`addon-${a.id}`}
                onClick={() => toggleAddon(a)}
                className={cn(
                  'flex items-start gap-3 rounded-xl p-3 text-start transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                  on ? 'ring-[1.5px] ring-foreground' : 'ring-1 ring-foreground/10 hover:bg-muted/50',
                )}
              >
                <PlanIcon Icon={a.Icon} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{addonTitle}</span>
                  <span className="block text-xs text-muted-foreground">{labels.addonDesc(a)}</span>
                </span>
                {on ? (
                  <CheckCircle2 className="size-4 shrink-0" aria-hidden />
                ) : (
                  <Circle className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                )}
              </button>
            );
          })}
        </div>
      </SectionCard>

      <SaveBar
        dirty={dirty}
        saving={saving}
        disabled={missingRequired}
        onSave={handleSave}
        saveLabel={isNew ? t('PLANS.ADMIN.CREATE') : t('PLANS.ADMIN.SAVE')}
        hint={missingRequired ? t('ADMIN_PLANS.EDIT.REQUIRED_HINT') : undefined}
        onCancel={() => navigate('/plans')}
        saveTestId="save-button"
      />
    </AdminPage>
  );
};

export default EditPlanPage;
